/**
 * SourceFromSuppliersModal
 * ------------------------
 * Admin-only modal that lets the user select suppliers and broadcast a backorder
 * RFQ to all of them via an Outlook draft (BCC'd).
 *
 * Workflow:
 *   1. On open, GET /api/backorder-sourcing/{backorderId}/preview → returns
 *      the backorder + supplier list + draft subject/body.
 *   2. Admin picks suppliers (search, select-all, by-category) and tweaks
 *      the subject/body if desired.
 *   3. Clicks "Open Outlook Draft" → POST .../broadcast with the supplier_ids;
 *      backend returns a mailto: URL with all suppliers BCC'd. We pop it.
 */

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Search, Send, X, Mail, AlertCircle, Check, Wrench, Building2 } from "lucide-react";
import { SUPPLIER_CATEGORIES, labelForCategory } from "@/data/supplierCategories";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SourceFromSuppliersModal({ backorderId, token, onClose, onBroadcast }) {
  const [loading, setLoading] = useState(true);
  const [broadcasting, setBroadcasting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  // Vendor pool toggle — both pools are always available; M&R just lands on
  // contractors first. We keep BOTH sets of selected IDs in separate slots so
  // switching tabs doesn't wipe selections.
  const [vendorTab, setVendorTab] = useState("supplier");
  const [selectedSuppliers, setSelectedSuppliers] = useState(new Set());
  const [selectedContractors, setSelectedContractors] = useState(new Set());
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const isMR = preview?.order_type === "maintenance_repair";
  const selected = vendorTab === "contractor" ? selectedContractors : selectedSuppliers;
  const setSelected = vendorTab === "contractor" ? setSelectedContractors : setSelectedSuppliers;
  const totalSelected = selectedSuppliers.size + selectedContractors.size;

  // Load preview data on mount
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/api/backorder-sourcing/${backorderId}/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (!alive) return;
        setPreview(r.data);
        setSubject(r.data.draft_subject || "");
        setBody(r.data.draft_body || "");
        // Default to contractor tab when this is an M&R backorder so the
        // admin lands on the more-likely-to-be-used pool.
        if (r.data.order_type === "maintenance_repair") {
          setVendorTab("contractor");
        }
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Failed to load suppliers");
        if (alive) onClose?.();
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [backorderId, token, onClose]);

  // Build a category list that prefers the curated taxonomy first, then
  // appends any free-text custom tags present on suppliers.
  const allCategories = useMemo(() => {
    const sourceList = vendorTab === "contractor"
      ? (preview?.contractors || [])
      : (preview?.suppliers || []);
    const present = new Set();
    sourceList.forEach((s) => (s.categories || []).forEach((c) => present.add(c)));
    const curatedPresent = SUPPLIER_CATEGORIES.filter((c) => present.has(c.id)).map((c) => c.id);
    const customPresent = Array.from(present)
      .filter((id) => !SUPPLIER_CATEGORIES.find((c) => c.id === id))
      .sort();
    return [...curatedPresent, ...customPresent];
  }, [preview, vendorTab]);

  const visibleSuppliers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sourceList = vendorTab === "contractor"
      ? (preview?.contractors || [])
      : (preview?.suppliers || []);
    return sourceList.filter((s) => {
      if (categoryFilter !== "all" && !(s.categories || []).includes(categoryFilter)) return false;
      if (!q) return true;
      const hay = `${s.name || ""} ${s.email || ""} ${s.contact_name || ""} ${(s.categories || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [preview, search, categoryFilter, vendorTab]);

  const allVisibleSelected = visibleSuppliers.length > 0 && visibleSuppliers.every((s) => selected.has(s.id));
  const toggleAllVisible = () => {
    const next = new Set(selected);
    if (allVisibleSelected) {
      visibleSuppliers.forEach((s) => next.delete(s.id));
    } else {
      visibleSuppliers.forEach((s) => next.add(s.id));
    }
    setSelected(next);
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleBroadcast = async () => {
    if (totalSelected === 0) {
      toast.error("Select at least one supplier or contractor first");
      return;
    }
    setBroadcasting(true);
    try {
      const r = await axios.post(
        `${API}/api/backorder-sourcing/${backorderId}/broadcast`,
        {
          supplier_ids: Array.from(selectedSuppliers),
          contractor_ids: Array.from(selectedContractors),
          subject,
          body,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const mailto = r.data?.mailto;
      const count = r.data?.recipient_count || 0;
      if (mailto) {
        // Pop Outlook (desktop / web / Apple Mail). Using location.href triggers
        // the OS mail-handler more reliably than window.open() across browsers.
        window.location.href = mailto;
        toast.success(`Outlook draft opened — ${count} vendor${count === 1 ? "" : "s"} BCC'd. Click Send when ready.`);
        onBroadcast?.(r.data);
        // Close after a short delay so the toast is visible
        setTimeout(() => onClose?.(), 800);
      } else {
        toast.error("Broadcast succeeded but no mailto link returned");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to broadcast");
    } finally {
      setBroadcasting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="source-suppliers-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white rounded-xl w-full max-w-5xl my-6 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isMR ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>
              {isMR ? <Wrench className="w-5 h-5" /> : <Mail className="w-5 h-5" />}
            </div>
            <div>
              <h3 className="font-bold text-lg text-slate-900">
                {isMR ? "Source from Contractors & Suppliers" : "Source from Suppliers & Contractors"}
              </h3>
              <p className="text-xs text-slate-500">
                Send a BCC'd RFQ via Outlook — {isMR ? "M&R work order " : ""}#{backorderId}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="source-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="px-6 py-16 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading suppliers, contractors and draft…
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-0">
            {/* LEFT: vendor picker */}
            <div className="border-r border-slate-200 max-h-[70vh] overflow-y-auto">
              <div className="p-4 sticky top-0 bg-white border-b border-slate-200 z-10 space-y-2">
                <div className="flex gap-1.5" data-testid="source-vendor-tabs">
                    <button
                      type="button"
                      onClick={() => setVendorTab("contractor")}
                      className={`flex-1 px-2 py-1.5 rounded text-xs font-bold transition-colors inline-flex items-center justify-center gap-1.5 ${
                        vendorTab === "contractor"
                          ? "bg-indigo-600 text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                      data-testid="source-vendor-tab-contractor"
                    >
                      <Wrench className="w-3.5 h-3.5" />
                      Contractors ({preview?.contractors?.length || 0})
                      {selectedContractors.size > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 rounded-full bg-white/30 text-[10px] font-bold px-1">
                          {selectedContractors.size}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setVendorTab("supplier")}
                      className={`flex-1 px-2 py-1.5 rounded text-xs font-bold transition-colors inline-flex items-center justify-center gap-1.5 ${
                        vendorTab === "supplier"
                          ? "bg-amber-600 text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                      data-testid="source-vendor-tab-supplier"
                    >
                      <Building2 className="w-3.5 h-3.5" />
                      Suppliers ({preview?.suppliers?.length || 0})
                      {selectedSuppliers.size > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 rounded-full bg-white/30 text-[10px] font-bold px-1">
                          {selectedSuppliers.size}
                        </span>
                      )}
                    </button>
                  </div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder={vendorTab === "contractor" ? "Search contractors by name, email, category…" : "Search suppliers by name, email, category…"}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-9 text-sm"
                    data-testid="source-supplier-search"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white"
                    data-testid="source-category-filter"
                  >
                    <option value="all">All categories</option>
                    {allCategories.map((c) => (
                      <option key={c} value={c}>{labelForCategory(c)}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={toggleAllVisible}
                    className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                    data-testid="source-select-all"
                  >
                    {allVisibleSelected ? "Deselect all visible" : `Select all (${visibleSuppliers.length})`}
                  </button>
                  <div className="ml-auto text-xs font-bold text-amber-700">
                    {selected.size} selected
                  </div>
                </div>
              </div>

              <ul className="divide-y divide-slate-100">
                {visibleSuppliers.length === 0 && (
                  <li className="px-4 py-10 text-center text-sm text-slate-500">
                    No suppliers match your filter.
                  </li>
                )}
                {visibleSuppliers.map((s) => {
                  const isSelected = selected.has(s.id);
                  return (
                    <li
                      key={s.id}
                      onClick={() => toggleOne(s.id)}
                      className={`px-4 py-2.5 cursor-pointer transition-colors ${
                        isSelected ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-slate-50"
                      }`}
                      data-testid={`source-supplier-row-${s.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          isSelected ? "bg-amber-500 border-amber-500 text-white" : "border-slate-300"
                        }`}>
                          {isSelected && <Check className="w-3 h-3" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900 truncate">{s.name}</span>
                            {!s.is_active && (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">Inactive</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 truncate">{s.email}</div>
                          {(s.categories || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(s.categories || []).slice(0, 4).map((c) => (
                                <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                                  {labelForCategory(c)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* RIGHT: draft email preview */}
            <div className="flex flex-col max-h-[70vh]">
              <div className="p-4 space-y-3 overflow-y-auto flex-1">
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-1">Subject</label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="source-subject"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-1">Email body</label>
                  <Textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={16}
                    className="text-xs font-mono leading-relaxed"
                    data-testid="source-body"
                  />
                  <p className="text-[11px] text-slate-500 mt-1.5">
                    The link in the email is keyed to backorder #{backorderId} — suppliers landing there see a passwordless quote form.
                  </p>
                </div>
                <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2.5 text-xs text-blue-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    All <strong>{totalSelected}</strong> selected vendor{totalSelected === 1 ? "" : "s"} will be placed on <strong>BCC</strong>. None of them will see the others on the email. The To: field is intentionally left empty (Outlook will use your own address).
                    {selectedSuppliers.size > 0 && selectedContractors.size > 0 && (
                      <span className="block mt-1 text-blue-900">
                        Selected: <strong>{selectedContractors.size}</strong> contractor{selectedContractors.size === 1 ? "" : "s"} + <strong>{selectedSuppliers.size}</strong> supplier{selectedSuppliers.size === 1 ? "" : "s"}.
                      </span>
                    )}
                  </span>
                </div>
              </div>

              <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 flex items-center justify-end gap-2">
                <Button variant="outline" onClick={onClose} disabled={broadcasting} data-testid="source-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={handleBroadcast}
                  disabled={broadcasting || totalSelected === 0}
                  className={`${isMR ? "bg-indigo-600 hover:bg-indigo-700" : "bg-amber-600 hover:bg-amber-700"} gap-2`}
                  data-testid="source-broadcast"
                >
                  {broadcasting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Opening Outlook…</>
                  ) : (
                    <><Send className="w-4 h-4" /> Open Outlook Draft ({totalSelected})</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
